import { useCallback } from 'react';
import { Modal, message } from 'antd';

import { createPublishTask, preparePublishPreflight } from '../../api';
import { resolveErrorMessage } from '../../utils/errorMessage';
import { getPublishPreflightMessages } from '../../utils/preflight';
import { getOperatorScopeId } from '../../utils/operatorScope';
import { buildTaskFiltersFromSelection, buildTaskSearchParams } from './helpers';

export const useCreateTaskFlow = ({
  refreshQuota,
  quota,
  resolveTaskCreateBlock,
  navigate,
  publishTimeForm,
  scheduleParseResult,
  selectedAccounts,
  selectedFilters,
  user,
  setPublishTimeDrawerVisible,
  setPublishAccountDrawerVisible,
  setCreateTaskDrawerVisible,
  filterForm,
  accountFilterForm,
  setScheduleParseResult,
  setSelectedFilters,
  setSelectedAccounts,
  setSelectedAccountKeys,
  form,
  paginationPageSize,
  loadData,
}) => {
  const renderPreflightIssueContent = useCallback((items = []) => {
    const lines = getPublishPreflightMessages({ blockers: items }, 'blockers', 6);
    return (
      <div>
        {lines.map((line) => (
          <div key={line} style={{ marginBottom: 8 }}>{line}</div>
        ))}
      </div>
    );
  }, []);
  const operatorId = getOperatorScopeId();

  const runTaskPreflight = useCallback(async (params = {}) => {
    const response = await preparePublishPreflight(params);
    if (response?.status !== 'success') {
      throw new Error(resolveErrorMessage(response?.error_message || response?.message, '发布前检查失败'));
    }
    return response;
  }, []);

  const handleCreateTask = useCallback(async function doCreateTask({ skipWarningConfirm = false } = {}) {
    const latestQuota = await refreshQuota();
    const createBlock = resolveTaskCreateBlock(latestQuota || quota);
    if (createBlock.blocked) {
      message.warning(`${createBlock.message} 正在前往付费订阅页面...`);
      navigate('/billing');
      return;
    }
    try {
      const values = await publishTimeForm.validateFields(['task_name', 'schedule_text', 'max_items_per_account_per_run', 'max_concurrency']);
      if (!scheduleParseResult || !scheduleParseResult.success) {
        message.warning('请先解析发布时间文本');
        return;
      }
      if (!selectedAccounts || selectedAccounts.length === 0) {
        message.warning('请选择发布账号');
        return;
      }
      if (!selectedFilters) {
        message.warning('请选择筛选条件');
        return;
      }

      const filters = buildTaskFiltersFromSelection(selectedFilters);
      const targetPlatforms = [...new Set(selectedAccounts.map((account) => account.platform))];
      const targetAccountIds = selectedAccounts.map((account) => account.account_id);

      const preflight = await runTaskPreflight({
        operator_id: operatorId || undefined,
        intent: 'task_create',
        target_platforms: targetPlatforms,
        target_account_ids: targetAccountIds,
      });
      if (!preflight.ready) {
        Modal.error({
          title: '当前无法创建发布任务',
          content: renderPreflightIssueContent(preflight.blockers),
        });
        return;
      }
      if (!skipWarningConfirm && Array.isArray(preflight.warnings) && preflight.warnings.length > 0) {
        Modal.confirm({
          title: '当前任务存在待处理风险，仍要继续创建吗？',
          content: renderPreflightIssueContent(preflight.warnings),
          okText: '仍然创建',
          cancelText: '取消',
          onOk: () => doCreateTask({ skipWarningConfirm: true }),
        });
        return;
      }

      const parsedSchedule = scheduleParseResult.data?.schedule || {};
      const parsedTriggerRaw = String(parsedSchedule.trigger_type || '').trim().toLowerCase();
      const parsedTrigger = parsedTriggerRaw === 'fixed_time' ? 'once' : parsedTriggerRaw;
      const taskType = parsedTrigger === 'cron'
        ? 'cron'
        : parsedTrigger === 'interval'
          ? 'interval'
          : 'once';

      const creatorUserId = user?.wechat?.openid || user?.user_id || 'aispire';
      const creatorUserName = user?.nickname || user?.wechat?.nickname || user?.name || null;
      const createParams = {
        task_name: values.task_name,
        task_type: taskType,
        creator_user_id: creatorUserId,
        creator_user_name: creatorUserName || undefined,
        status: 'active',
        max_items_per_account_per_run: values.max_items_per_account_per_run || 1,
        max_concurrency: values.max_concurrency || 2,
        schedule: {
          trigger_type: taskType,
          timezone: parsedSchedule.timezone || 'Asia/Shanghai',
          cron_expression: taskType === 'cron' ? (parsedSchedule.cron_expression || null) : null,
          interval_seconds: taskType === 'interval' ? (parsedSchedule.interval_seconds || null) : null,
          start_at: taskType === 'interval' ? (parsedSchedule.start_at || null) : null,
          end_at: taskType === 'interval' ? (parsedSchedule.end_at || null) : null,
          run_at: taskType === 'once' ? (parsedSchedule.run_at || null) : null,
        },
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        target_platforms: targetPlatforms,
        target_account_ids: targetAccountIds,
        execution_policy: 'cloud_preferred',
        allowed_agent_types: ['local', 'cloud'],
        fallback_policy: 'manual_only',
        retry_strategy: { max_switch_retry: 1, cooldown_sec: 300 },
      };

      const response = await createPublishTask(createParams);
      if (response.status === 'success') {
        message.success('创建发布任务成功');
        setPublishTimeDrawerVisible(false);
        setPublishAccountDrawerVisible(false);
        setCreateTaskDrawerVisible(false);
        publishTimeForm.resetFields();
        filterForm.resetFields();
        accountFilterForm.resetFields();
        setScheduleParseResult(null);
        setSelectedFilters(null);
        setSelectedAccounts([]);
        setSelectedAccountKeys([]);

        refreshQuota().catch(() => {});
        const formValues = form.getFieldsValue();
        const searchParams = buildTaskSearchParams(formValues, 1, paginationPageSize);
        loadData(searchParams, 1, paginationPageSize);
      } else {
        message.error(resolveErrorMessage(response.message, '创建发布任务失败'));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('创建发布任务失败:', error);
      message.error('创建发布任务失败，请稍后重试');
    }
  }, [
    refreshQuota,
    resolveTaskCreateBlock,
    quota,
    navigate,
    publishTimeForm,
    scheduleParseResult,
    selectedAccounts,
    selectedFilters,
    runTaskPreflight,
    operatorId,
    renderPreflightIssueContent,
    user,
    setPublishTimeDrawerVisible,
    setPublishAccountDrawerVisible,
    setCreateTaskDrawerVisible,
    filterForm,
    accountFilterForm,
    setScheduleParseResult,
    setSelectedFilters,
    setSelectedAccounts,
    setSelectedAccountKeys,
    form,
    paginationPageSize,
    loadData,
  ]);

  return {
    handleCreateTask,
  };
};
